import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";
import { ROUTE_CONFIG } from "@/routes/config";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { EOnboardingStep } from "@/enum/onboarding-step.enum";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useCreateListMutation,
	useDeleteListMutation,
	useLazyGetListQuery,
} from "@/pages/Private/redux/list/list.api";
import { useUpdateApolloMutation } from "@/pages/Private/redux/apollo/apollo.api";
import { useCreateJobMutation } from "@/pages/Private/redux/job/job.api";
import { useCreateCompanyEnrichMutation } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { useCreateLiPostMutation } from "@/pages/Private/redux/liPost/liPost.api";
import { useCreateMapMutation } from "@/pages/Private/redux/map/map.api";
import { useCreateParallelAiMutation } from "@/pages/Private/redux/parallelAi/parallelAi.api";
import { useCreateFundingMaMutation } from "@/pages/Private/redux/fundingMa/fundingMa.api";
import { useUpdateOnboardingStepMutation } from "@/pages/Private/redux/account/account.api";

import { OnboardingStepLoading } from "../components/OnboardingStepLoading";

import { OnboardingListRow } from "../components/lists/OnboardingListRow";
import { OnboardingRecommendationNotice } from "../components/lists/OnboardingRecommendationNotice";
import { syncOnboardingLists } from "../components/lists/syncOnboardingLists";
import { useOnboardingFilterOptions } from "../components/lists/configs/useOnboardingFilterOptions";
import { useOnboardingListPicker } from "../components/lists/useOnboardingListPicker";
import {
	ONBOARDING_LIST_SECTIONS,
	ONBOARDING_LIST_TYPES,
	ONBOARDING_LISTS_MAX,
	OnboardingListTypeDef,
	OnboardingListTypeKey,
} from "../components/lists/onboardingListTypes";

type Screen = "featured" | "catalog";

const TOAST_MS = 2800;

/** The catalog screen groups the types the way the in-app create flow does. */
const sectionsWithTypes = (): { section: string; types: OnboardingListTypeDef[] }[] =>
	ONBOARDING_LIST_SECTIONS.map((section) => ({
		section,
		types: ONBOARDING_LIST_TYPES.filter((definition) => definition.sectionKey === section),
	})).filter((entry) => entry.types.length > 0);

/**
 * Step 4a of the pre-checkout onboarding (PREC-3653 phase 3): pick up to two
 * list types (two ICP-based recommendations up front, the full pre-checkout
 * catalog behind "build other lists") and create them as real lists on the
 * demo credits.
 */
export const OnboardingLists: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.lists.${key}`);

	const navigate = useNavigate();
	const { user } = useAppSelector(authSelector);

	const [createList] = useCreateListMutation();
	const [deleteList] = useDeleteListMutation();
	const [createJob] = useCreateJobMutation();
	const [createCompanyEnrich] = useCreateCompanyEnrichMutation();
	const [createLiPost] = useCreateLiPostMutation();
	const [createMap] = useCreateMapMutation();
	const [createParallelAi] = useCreateParallelAiMutation();
	const [createFundingMa] = useCreateFundingMaMutation();
	const [getList] = useLazyGetListQuery();
	const [updateApollo] = useUpdateApolloMutation();
	const [updateOnboardingStep] = useUpdateOnboardingStepMutation();

	const [screen, setScreen] = useState<Screen>("featured");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showErrors, setShowErrors] = useState(false);
	const [toast, setToast] = useState<string | null>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const showToast = (message: string) => {
		setToast(message);

		if (toastTimerRef.current) {
			clearTimeout(toastTimerRef.current);
		}

		toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);
	};

	// Without this the pending hide-toast timer fires after the component is
	// gone and sets state on an unmounted tree.
	useEffect(
		() => () => {
			if (toastTimerRef.current) {
				clearTimeout(toastTimerRef.current);
			}
		},
		[]
	);

	const filterOptions = useOnboardingFilterOptions();
	const picker = useOnboardingListPicker(filterOptions, () => showToast(ts("maxToast")));
	const { selections, selectedKeys, errorsByKey, allComplete } = picker;

	const handleContinue = async () => {
		if (isSubmitting) {
			return;
		}

		// Continue stays clickable so the blocking fields can be pointed out —
		// a disabled button leaves the user guessing what is missing.
		if (!allComplete) {
			setShowErrors(true);
			showToast(ts(selectedKeys.length === 0 ? "pickOneToast" : "fixFieldsToast"));

			return;
		}

		setIsSubmitting(true);

		try {
			// Coming back from Track and continuing again must not duplicate the
			// lists that were already created — only what changed is touched.
			await syncOnboardingLists({
				userId: user?.id ?? 0,
				selections,
				listNameFor: (key) => t(`onboarding.lists.types.${key}.title`),
				deleteList,
				mutations: {
					createList,
					createJob,
					createCompanyEnrich,
					createLiPost,
					createMap,
					createParallelAi,
					createFundingMa,
					getList,
					updateApollo,
				},
			});

			await updateOnboardingStep({ step: EOnboardingStep.TRACK }).unwrap();
			navigate(ROUTE_CONFIG.ONBOARDING_TRACK);
		} catch (err) {
			console.error(err);
			showToast(ts("createFailed"));
			setIsSubmitting(false);
		}
	};

	const renderRow = (key: OnboardingListTypeKey) => (
		<OnboardingListRow
			key={key}
			config={selections[key] ?? null}
			errors={showErrors ? errorsByKey[key] : undefined}
			options={filterOptions}
			typeKey={key}
			onConfigChange={(config) => picker.updateConfig(key, config)}
			onToggle={() => picker.toggleType(key)}
		/>
	);

	// Nothing is rendered before the prefill lands — see `OnboardingStepLoading`.
	if (!picker.isReady) {
		return (
			<OnboardingStepLoading subtitle={ts("preparingSubtitle")} title={ts("preparingTitle")} />
		);
	}

	return (
		<div className="w-full flex flex-col items-center justify-center">
			<div className="relative flex max-h-[720px] w-[760px] max-w-[100%] flex-col rounded-[20px] bg-white shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)]">
				<div className="flex-1 overflow-y-auto px-8 pb-4 pt-8">
					<div className="mb-4 flex justify-center">
						<img alt="logo" className="h-10 w-10" src={AppLogo} />
					</div>
					<div className="mb-1 text-center text-md font-bold">
						{screen === "featured" ? ts("titleFeatured") : ts("titleCatalog")}
					</div>
					<div className="mb-5 text-center text-ssm text-text-meta">
						{screen === "featured" ? ts("subtitleFeatured") : ts("subtitleCatalog")}
					</div>

					<OnboardingRecommendationNotice
						hasFailed={picker.recommendationFailed}
						hasUnapplied={picker.hasUnappliedRecommendation}
						isRetrying={picker.isRetryingRecommendation}
						onApply={picker.applyRecommendation}
						onRetry={picker.retryRecommendation}
					/>

					{screen === "featured"
						? picker.featuredKeys.map(renderRow)
						: sectionsWithTypes().map(({ section, types }) => (
								<div key={section} className="mb-4">
									<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-meta">
										{ts(`sections.${section}`)}
									</div>
									{types.map((definition) => renderRow(definition.key))}
								</div>
							))}

					{screen === "featured" ? (
						<button
							className="mt-2 w-full cursor-pointer rounded-xl border border-dashed border-gray-200 px-4 py-3 text-ssm font-semibold text-text-meta transition-colors duration-200 hover:border-brand-light hover:text-brand-light"
							type="button"
							onClick={() => setScreen("catalog")}
						>
							{ts("buildOther")}
						</button>
					) : (
						<button
							className="mt-2 cursor-pointer text-xs font-semibold text-brand-light"
							type="button"
							onClick={() => setScreen("featured")}
						>
							← {ts("backToRecommended")}
						</button>
					)}
				</div>

				<div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
					<span className="text-xs font-semibold text-text-meta">
						{t("onboarding.lists.pickedCount", {
							count: selectedKeys.length,
							max: ONBOARDING_LISTS_MAX,
						})}
					</span>
					<Button
						disabled={isSubmitting}
						isLoading={isSubmitting}
						size={ButtonSize.ML}
						title={ts("continue")}
						onClick={handleContinue}
					/>
				</div>

				{toast && (
					<div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-xs text-white">
						{toast}
					</div>
				)}
			</div>
		</div>
	);
};
